import { Clock, GripVertical, Plus, User } from "lucide-react"
import { useState } from "react"
import { cx } from "../lib/cx"
import type { ProjectTask } from "../types"

type ProjectTaskKanbanProps = {
  tasks: ProjectTask[]
  onStatusChange: (taskId: string, newStatus: ProjectTask["status"]) => void
  onTaskClick: (taskId: string) => void
  onAddTask: (title: string) => void
}

const columns: Array<{ id: ProjectTask["status"]; label: string; dotColor: string }> = [
  { id: "todo", label: "To Do", dotColor: "bg-slate-400" },
  { id: "in_progress", label: "In Progress", dotColor: "bg-blue-400" },
  { id: "review", label: "Review", dotColor: "bg-yellow-400" },
  { id: "done", label: "Done", dotColor: "bg-green-400" },
]

const priorityColors: Record<string, string> = {
  high: "bg-rose-100 text-rose-700",
  medium: "bg-yellow-100 text-yellow-700",
  low: "bg-slate-100 text-slate-700",
}

const ProjectTaskKanban = ({ tasks, onStatusChange, onTaskClick, onAddTask }: ProjectTaskKanbanProps) => {
  const [draggedId, setDraggedId] = useState<string | null>(null)
  const [showNewTask, setShowNewTask] = useState(false)
  const [newTitle, setNewTitle] = useState("")

  const handleDragStart = (e: React.DragEvent, taskId: string) => {
    setDraggedId(taskId)
    e.dataTransfer.effectAllowed = "move"
    e.dataTransfer.setData("text/plain", taskId)
  }

  const handleDrop = (e: React.DragEvent, status: ProjectTask["status"]) => {
    e.preventDefault()
    const taskId = e.dataTransfer.getData("text/plain") || draggedId
    if (taskId) {
      onStatusChange(taskId, status)
    }
    setDraggedId(null)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = "move"
  }

  const handleAddTask = () => {
    if (newTitle.trim()) {
      onAddTask(newTitle.trim())
      setNewTitle("")
      setShowNewTask(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {columns.map((col) => {
          const colTasks = tasks.filter((t) => t.status === col.id)
          return (
            <div key={col.id} className="flex flex-col">
              {/* Column header */}
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={cx("h-2.5 w-2.5 rounded-full", col.dotColor)} />
                  <span className="text-sm font-semibold text-slate-700">{col.label}</span>
                </div>
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                  {colTasks.length}
                </span>
              </div>

              {/* Drop zone */}
              <div
                className={cx(
                  "min-h-[120px] flex-1 rounded-xl border-2 border-dashed p-2 transition",
                  draggedId ? "border-moss/40 bg-moss/5" : "border-slate-200 bg-slate-50/50",
                )}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, col.id)}
              >
                <div className="space-y-2">
                  {colTasks.map((task) => (
                    <div
                      key={task.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, task.id)}
                      onDragEnd={() => setDraggedId(null)}
                      onClick={() => onTaskClick(task.id)}
                      className={cx(
                        "group cursor-pointer rounded-lg border border-line bg-white p-3 shadow-sm transition hover:shadow-md",
                        draggedId === task.id && "opacity-50",
                      )}
                    >
                      <div className="flex items-start gap-2">
                        <GripVertical className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-stone opacity-0 transition group-hover:opacity-100" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{task.title}</p>
                          {task.description && (
                            <p className="mt-0.5 text-xs text-stone line-clamp-2">{task.description}</p>
                          )}
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            {task.priority && (
                              <span className={cx("rounded-full px-1.5 py-0.5 text-xs font-medium", priorityColors[task.priority])}>
                                {task.priority}
                              </span>
                            )}
                            {task.assignee && (
                              <div className="flex items-center gap-1 text-xs text-stone">
                                <User className="h-3 w-3" />
                                <span className="truncate max-w-[80px]">{task.assignee}</span>
                              </div>
                            )}
                            {task.estimatedHours ? (
                              <div className="flex items-center gap-1 text-xs text-stone">
                                <Clock className="h-3 w-3" />
                                <span>{task.estimatedHours}h</span>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {colTasks.length === 0 && (
                    <div className="py-6 text-center text-xs text-stone">
                      {draggedId ? "Drop here" : "No tasks"}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Add task */}
      {showNewTask ? (
        <div className="flex gap-2">
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAddTask()
              if (e.key === "Escape") { setNewTitle(""); setShowNewTask(false) }
            }}
            placeholder="Task title..."
            className="flex-1 rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-moss"
            autoFocus
          />
          <button
            type="button"
            onClick={handleAddTask}
            className="rounded-lg bg-moss px-4 py-2 text-sm font-medium text-white hover:bg-moss/90"
          >
            Add
          </button>
          <button
            type="button"
            onClick={() => { setNewTitle(""); setShowNewTask(false) }}
            className="rounded-lg border border-line bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowNewTask(true)}
          className="flex items-center gap-2 rounded-lg border border-dashed border-line bg-slate-50 px-4 py-2.5 text-sm font-medium text-stone transition hover:bg-slate-100 hover:text-slate-700"
        >
          <Plus className="h-4 w-4" />
          Add Task
        </button>
      )}
    </div>
  )
}

export default ProjectTaskKanban
